import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import * as yup from 'yup'
import { Form, Formik, Field } from 'formik'
import { validateField } from '@src/utils/validators'
import { searchResultsRoute, useRouter, documentsSearch } from '@src/routes'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import {
  Typography,
  Theme,
  Button,
  Link,
  useMediaQuery,
} from '@material-ui/core'
import {
  SearchFocusType,
  SearchType,
  getValidSearch,
  ProductSearchType,
  GeneSearchType,
} from '@utils/searchUtils'
import { useCurrentUser, SitePreference } from '@utils/useCurrentUser'
import LiquidSelectAdapter from '@src/components/LiquidSelectAdapter'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import vrStyles from '@src/styles/utils/vrStyles'
import DocumentLibraryBanner from '../DocumentSearch/DocumentLibraryBanner'
import messages from '@src/utils/messages'
import { sendAdvancedSearchedTermEvent } from '@utils/analytics'

export const useStyles = makeStyles((theme: Theme) => {
  const { vr4, vr6, vr8 } = vrStyles
  return {
    submitButton: {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: 'auto',
      },
    },
    vr4,
    vr6,
    vr8,
    medium: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    instructionsText: {
      fontSize: theme.typography.pxToRem(14),
      ...vr4,
      [theme.breakpoints.up('sm')]: {
        fontSize: theme.typography.pxToRem(16),
        ...vr6,
      },
    },
  }
})

const validB2BProductSearchOptions = [
  ProductSearchType.ProductName,
  ProductSearchType.CasNumber,
  ProductSearchType.ProductNumber,
  ProductSearchType.MolecularFormula,
  ProductSearchType.MdlNumber,
]

const geneSearchOptions = [
  GeneSearchType.RefSeq,
  GeneSearchType.GeneDesc,
  GeneSearchType.GeneId,
  GeneSearchType.GeneSymbol,
  GeneSearchType.TrcNumber,
]

// sequenced in alphabetical order in FIND-1099
const searchOptionsMap = new Map<
  ProductSearchType | GeneSearchType | SearchType,
  string
>()
searchOptionsMap.set(ProductSearchType.CasNumber, 'e.g. 77-86-1')
searchOptionsMap.set(
  SearchType.Chromatograms,
  'e.g. Dihydrocapsaicin in Chili Powder'
)
searchOptionsMap.set(ProductSearchType.ColorIndex, 'e.g. 10790')
searchOptionsMap.set(ProductSearchType.EcNumber, 'e.g. 3.4.21.5')
searchOptionsMap.set(ProductSearchType.EgecNumber, 'e.g. 200-315-5')
searchOptionsMap.set(ProductSearchType.FemaNumber, 'e.g. 3026')
searchOptionsMap.set(GeneSearchType.GeneDesc, 'e.g. phosphatase')
searchOptionsMap.set(GeneSearchType.GeneId, 'e.g. 69181')
searchOptionsMap.set(GeneSearchType.GeneSymbol, 'e.g. Map3k10')
searchOptionsMap.set(ProductSearchType.MdlNumber, 'e.g. MFCD00011232')
searchOptionsMap.set(ProductSearchType.MolecularFormula, 'e.g. BaTiO3')
searchOptionsMap.set(SearchType.Papers, 'e.g. akt antibodies')
searchOptionsMap.set(ProductSearchType.ProductName, 'e.g. Azophloxine')
searchOptionsMap.set(ProductSearchType.ProductNumber, 'e.g. T1503')
searchOptionsMap.set(GeneSearchType.RefSeq, 'e.g.  NM_001126117')
searchOptionsMap.set(GeneSearchType.TrcNumber, 'e.g. TRCN0000030720')

interface ProductSearchFormValues {
  type: string
  term: string
}

const ProductSearchForm = () => {
  const [searchType, setSearchType] = useState<
    ProductSearchType | GeneSearchType
  >(ProductSearchType.ProductName)
  const classes = useStyles()
  const router = useRouter()
  const { formatMessage } = useIntl()
  const { getSitePreference } = useCurrentUser()
  const currentUserOrgId = getSitePreference(SitePreference.CatalogFilter)
  const searchOptions = currentUserOrgId
    ? validB2BProductSearchOptions
    : Array.from(searchOptionsMap.keys())

  const handleSendAdvancedSearchedTermEvent = (
    values: ProductSearchFormValues,
    errors: string
  ) => {
    const { type, term } = values
    // if there is an error message use that
    // if search term still gets here empty - provide error message
    // otherwise no error message needed.
    const errorMessage = errors
      ? errors
      : !term
        ? formatMessage({
            id: 'SEARCH_TERM_REQUIRED',
            defaultMessage: 'Please enter at least one search term',
          })
        : ''
    sendAdvancedSearchedTermEvent({
      searchTerm: term.trim(),
      searchAutoSuggest: '',
      searchAutoSuggestTerm: '',
      searchType: type,
      searchComponent: 'advanced search',
      searchErrorMessage: errorMessage.toLowerCase(),
    })
  }

  const handleProductSearch = (values: ProductSearchFormValues) => {
    const { type, term } = values

    if (!term) return

    const getSearchFocus = (searchType: string): SearchFocusType => {
      switch (searchType) {
        case SearchType.Papers:
          return SearchFocusType.Papers
        case SearchType.Chromatograms:
          return SearchFocusType.Chromatograms
        default:
          return geneSearchOptions.includes(searchType as GeneSearchType)
            ? SearchFocusType.Genes
            : SearchFocusType.Products
      }
    }

    const focus = getSearchFocus(type)
    const trimmedTerm = term.trim()

    router.push(
      `${searchResultsRoute.searchResults(trimmedTerm)}${getValidSearch({
        term: trimmedTerm,
        focus,
        type,
        orgId: currentUserOrgId,
      })}`
    )
    handleSendAdvancedSearchedTermEvent(values, '')
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <div>
      <Typography className={classes.instructionsText}>
        <FormattedMessage
          id="THE_DATA_SPECIFIC_SEARCH"
          defaultMessage="Please select a search type and enter keywords for a more specific search of the product and content data."
        />
      </Typography>
      {isMobile && (
        <div className={classes.vr6}>
          <DocumentLibraryBanner>
            <Typography variant="body1">
              <FormattedMessage
                id="DOC_OR_CERT"
                defaultMessage="Looking for a <strong>document</strong> or <strong>certificate</strong>?"
                values={{
                  strong: (...chunks) => <strong>{chunks}</strong>,
                }}
              />
            </Typography>
            <Typography variant="body1">
              <span className={classes.medium}>
                <FormattedMessage id="TRY" defaultMessage="Try" />
              </span>{' '}
              <Link href={documentsSearch.index()}>
                <FormattedMessage id="DOCUMENTS" defaultMessage="Documents" />
              </Link>{' '}
              <span className={classes.medium}>
                <FormattedMessage id="INSTEAD" defaultMessage="instead" />
              </span>
              {'.'}
            </Typography>
          </DocumentLibraryBanner>
        </div>
      )}
      <Formik
        initialValues={{
          type: ProductSearchType.CasNumber,
          term: '',
        }}
        onSubmit={handleProductSearch}
      >
        {(props) => (
          <Form>
            <div className={classes.vr4}>
              <Field
                name="type"
                component={LiquidSelectAdapter}
                label={
                  <strong>
                    <FormattedMessage
                      id="SEARCH_TYPE"
                      defaultMessage="Search Type"
                    />
                  </strong>
                }
                data-testid="adv-search-product-type"
                options={searchOptions.map((opt) => ({
                  label: messages[opt.toUpperCase()]
                    ? formatMessage(messages[opt.toUpperCase()])
                    : opt,
                  value: opt,
                }))}
                onChange={(e) => {
                  props.handleChange(e)
                  setSearchType(e.target.value)
                }}
              />
            </div>
            <div className={classes.vr8}>
              <Field
                name="term"
                placeholder={searchOptionsMap.get(searchType)}
                component={LiquidInputAdapter}
                label={
                  <strong>
                    <FormattedMessage
                      id="SEARCH_TERM"
                      defaultMessage="Search Term"
                    />
                  </strong>
                }
                data-testid="adv-search-product-term"
                validate={validateField(
                  yup
                    .string()
                    .trim()
                    .required(
                      formatMessage({
                        id: 'SEARCH_TERM_REQUIRED',
                        defaultMessage: 'Please enter at least one search term',
                      })
                    )
                )}
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              className={classes.submitButton}
              data-testid="advanced-search-submit"
              id="advanced-search-submit"
              onClick={() => {
                if (props.errors.term || !props.values.term) {
                  handleSendAdvancedSearchedTermEvent(
                    props.values,
                    props.errors.term || ''
                  )
                }
              }}
            >
              <FormattedMessage id="SEARCH" defaultMessage="Search" />
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default ProductSearchForm
